import React from 'react';
import { Link } from 'react-router-dom';
import { POSTS } from '../data/posts';
import SEO from '../components/SEO';
import styles from './BlogListPage.module.css';

const BLOG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type':    'Blog',
  '@id':      'https://movementgreen.com.au/blog#blog',
  name:       'Movement Green Blog — Victorian Energy Upgrades',
  description:'Expert guides, rebate news, and tips on Victorian Energy Upgrades (VEU), heat pumps, reverse-cycle AC, LED lighting, commercial solar, and government rebates across Victoria.',
  url:        'https://movementgreen.com.au/blog',
  publisher: {
    '@id': 'https://movementgreen.com.au/#business',
    '@type': 'Organization',
    name:  'Movement Green',
  },
  blogPost: POSTS.map(p => ({
    '@type':       'BlogPosting',
    headline:      p.title,
    description:   p.description,
    datePublished: p.date,
    url:           `https://movementgreen.com.au/blog/${p.slug}`,
    author: { '@type': 'Organization', name: 'Movement Green' },
  })),
};

function CategoryBadge({ label }) {
  return <span className={styles.catBadge}>{label}</span>;
}

export default function BlogListPage() {
  const [featured, ...rest] = POSTS;

  return (
    <main className="page-enter">
      <SEO
        title="Blog — Victorian Energy Upgrades, VEU Rebates & Energy Tips | Movement Green"
        description="Expert guides on Victorian Energy Upgrades (VEU), heat pump rebates, reverse-cycle AC rebates, LED lighting, and commercial solar rebates for Victorian homes and businesses."
        keywords="VEU blog, Victorian Energy Upgrades blog, heat pump rebate guide, AC rebate Victoria guide, LED lighting VEU, energy efficiency tips Victoria"
        schema={BLOG_SCHEMA}
      />

      {/* ── HERO ─────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <span className="eyebrow">Movement Green Blog</span>
          <h1 className={`disp-xl ${styles.heroTitle}`}>
            VEU Guides,<br/>Rebate News &amp;<br/><em>Energy Tips.</em>
          </h1>
          <p className={`body-lg ${styles.heroDesc}`}>
            Everything Victorian homeowners need to know about government
            rebates, energy-efficient upgrades, and the VEU program.<sup>*</sup>
          </p>
        </div>
      </section>

      {/* ── FEATURED POST ────────────────────────────── */}
      <section className={styles.featuredSection}>
        <div className={styles.featuredInner}>
          <Link to={`/blog/${featured.slug}`} className={styles.featuredCard}>
            <div className={styles.featuredMeta}>
              <CategoryBadge label={featured.category} />
              <span className={styles.metaDate}>{formatDate(featured.date)}</span>
              <span className={styles.metaDot}>·</span>
              <span className={styles.metaRead}>{featured.readTime}</span>
            </div>
            <h2 className={`disp-md ${styles.featuredTitle}`}>{featured.title}</h2>
            <p className={styles.featuredDesc}>{featured.description}</p>
            <span className={styles.featuredCta}>Read Article →</span>
          </Link>
        </div>
      </section>

      {/* ── POST GRID ────────────────────────────────── */}
      <section className={styles.gridSection}>
        <div className={styles.gridInner}>
          <p className={styles.gridLabel}>All Articles</p>
          <div className={styles.grid}>
            {rest.map(post => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className={styles.card}>
                <div className={styles.cardTop}>
                  <CategoryBadge label={post.category} />
                </div>
                <h3 className={`h-lg ${styles.cardTitle}`}>{post.title}</h3>
                <p className={styles.cardDesc}>{post.description}</p>
                <div className={styles.cardMeta}>
                  <span className={styles.metaDate}>{formatDate(post.date)}</span>
                  <span className={styles.metaDot}>·</span>
                  <span className={styles.metaRead}>{post.readTime}</span>
                </div>
                <span className={styles.cardArrow}>→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <div>
            <span className="eyebrow-light">Ready to Upgrade?</span>
            <h2 className={`disp-md ${styles.ctaTitle}`}>
              See what rebates your<br/>home <em>qualifies for.</em>
            </h2>
          </div>
          <Link to="/contact" className="btn btn-accent btn-lg">Book Free Assessment →</Link>
        </div>
      </section>
    </main>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}
